
import { mapGetters, mapState } from "vuex"
import ImageCarousel from "@/components/business/ImageCarousel"
import BusinessHeader from "@/components/business/BusinessHeader"
import BusinessInfo from "@/components/business/BusinessInfo"
import breadcrumb from "@/mixins/breadcrumb"
import BusinessViewersSnackbar from "@/components/business/BusinessViewersSnackbar"
import JobList from "@/components/job/JobList"
import Button from "@/components/base/Button"
import traineeService from "@/services/trainee.service"
import chatSteps from "@/components/chat/chatSteps"
import FloatingChatButton from "@/components/chat/FloatingChatButton"
import { routeToBusiness } from "@/helpers/routing"
import { decodeString } from "@/helpers/utils"
import { bookTimeSlot } from "@/services/textprefill"

export default {
    components: {
        FloatingChatButton,
        ChatContainer: () => import("@/components/chat/ChatContainer"),
        ChatFormSuccess: () => import("@/components/chat/ChatFormSuccess"),
        Modal: () => import("@/components/base/modal/Modal"),
        ChatForm: () => import("@/components/application/interest/ChatForm"),
        BusinessViewersSnackbar,
        BusinessInfo,
        BusinessHeader,
        ImageCarousel,
        JobList,
        Button,
        BusinessLocation: () =>
            import("@/components/business/BusinessLocation"),
        PostList: () => import("@/components/post/PostList"),
        TimeSlotPicker: () => import("@/components/jobfair/TimeSlotPicker"),
    },
    mixins: [breadcrumb],
    fetch() {
        this.showChatComponent = this.$auth.loggedIn
        if ("nachricht_senden" in this.$route.query && this.$can.chat()) {
            this.sendMessageOnCreated = true
            this.showChatComponent = false
            this.isSending = true
            this.selectConversation()
            this.showChatModal = true
        }
    },
    async asyncData({ store, route, error, redirect }) {
        const businessPromise = store
            .dispatch("business/setCurrentDisplayedProfile", route.params.slug)
            .catch((errorResponse) => {
                if (
                    errorResponse?.response?.status === 303 &&
                    errorResponse?.response?.data?.slug
                ) {
                    redirect(
                        301,
                        routeToBusiness({
                            slug: errorResponse?.response?.data?.slug,
                        })
                    )
                } else {
                    error({
                        statusCode: 404,
                        message: "Der Betrieb wurde nicht gefunden.",
                    })
                }
            })
        const jobsPromise = store.dispatch(
            "business/loadJobs",
            route.params.slug
        )
        const postPromise = store.dispatch(
            "post/getPostsByBusiness",
            route.params.slug
        )
        /** Await in parallel - allSettled Polyfill */
        await Promise.all(
            [businessPromise, jobsPromise, postPromise].map((p) =>
                p
                    .then((value) => ({
                        status: "fulfilled",
                        value,
                    }))
                    .catch((reason) => ({
                        status: "rejected",
                        reason,
                    }))
            )
        )
    },
    layout(context) {
        return context.$auth.user && context.$auth.user.business
            ? "modern"
            : "detailpage"
    },
    meta: {
        toolbar: {
            persistent: true,
            keepSearch: true,
            hideBottomNav: true,
            detailType: "business",
        },
        sidebar: {
            dense: true,
        },
    },
    data: () => ({
        chatSteps: null,
        sendMessageOnCreated: false,
        isFloatingChatVisible: true,
        showChatComponent: false,
        showChatModal: false,
        isSending: false,
        showSuccessModal: false,
        loadingLikeModal: true,
        showLikeModal: false,
        likeOnOpen: false,
        firstname: "",
        lastname: "",
    }),
    computed: {
        ...mapState({
            business: (state) => state.business.displayedProfile.baseInfo,
            apprenticeships: (state) =>
                state.business.displayedProfile.apprenticeships,
            traineeships: (state) =>
                state.business.displayedProfile.traineeships,
        }),
        ...mapGetters(["webpSupported"]),
        ...mapState("post", ["posts"]),
        socialRedirectUrl() {
            let route = { ...this.$route.fullPath }
            route.query = { ...route.query, ...{ nachricht_senden: true } }
            return this.$router.resolve(route).href
        },
        logo() {
            return this.$getImage({
                image: this.business.logo,
                preferedSize: "3",
            })
        },
        showBusinessMap() {
            return this.business.primary_address?.street?.length > 0
        },
        showFloatingChat() {
            return (
                !this.$auth.loggedIn ||
                (!this.$auth.user.business && !this.$auth.user.school)
            )
        },
        schedule() {
            const routeSchedule = this.$route.query.schedule
            if (!routeSchedule) return null
            try {
                return JSON.parse(decodeString(this.$route.query.schedule))
            } catch (e) {
                return null
            }
        },
        jobFairName() {
            return this.$route.query.title
        },
        showTimePicker() {
            return this.schedule && this.schedule.length > 0
        },
    },
    mounted() {
        this.chatSteps = chatSteps

        if ("chat" in this.$route.query && this.$can.chat()) {
            this.openChat()
        }
    },
    methods: {
        async openChat() {
            this.isInRequest = false
            if (this.showChatModal) return
            await this.selectConversation()
            this.showChatModal = true
        },
        onChatAuth(formData) {
            this.firstname = formData.firstname.data
            this.lastname = formData.lastname.data
        },
        onChatFormInit(formData) {
            if (this.sendMessageOnCreated && this.$can.chat()) {
                this.selectConversation()
                this.chatSendRequest(formData)
                this.$router.replace(this.$route.path) // Remove query
            }
        },
        selectConversation() {
            return this.$store.dispatch("chat/selectConversation", {
                business: this.business,
            })
        },
        async chatSendRequest(formData) {
            if (!this.$auth.loggedIn || !this.$can.chat() || !formData) {
                this.handleSendRequestError(
                    {},
                    "Da ist etwas schief gelaufen. Bitte melde dich nochmal an."
                )
                return
            }
            try {
                this.isSending = true
                // send text message to conversation
                await this.$store.dispatch("chat/sendMessage", {
                    text: formData["messageText"].data ?? undefined,
                })

                // update trainee firstname/lastname
                const traineeId = this.$auth.user.trainee.id
                await traineeService
                    .update(traineeId, {
                        firstname: formData["firstname"].data ?? undefined,
                        lastname: formData["lastname"].data ?? undefined,
                    })
                    .catch((err) => console.warn(err))
                await this.$auth.fetchUser()
                this.$refs.chatForm?.remove()
                this.showChatModal = false
                this.showChatComponent = true
                this.showSuccessModal = true
                await this.$store.dispatch("chat/loadConversations")
            } catch (err) {
                console.error(err)
                this.handleSendRequestError(err)
            }
            this.isSending = false
        },
        onChatIntersect(e) {
            this.isFloatingChatVisible = !e[0].isIntersecting
        },
        getStructuredData() {
            return [
                {
                    "@context": "http://schema.org",
                    "@type": "Organization",
                    name: this.business.name,
                    legalName: this.business.name,
                    url: this.$config.baseURL + this.$route.path,
                    logo: this.$getImage({
                        image: this.business.logo,
                        preferedSize: "2",
                    }).src,
                    description: this.business.about_us,
                    address: {
                        "@type": "PostalAddress",
                        streetAddress: this.business.primary_address.street,
                        addressLocality: this.business.primary_address.city,
                        addressRegion: this.business.primary_address.state,
                        postalCode: this.business.primary_address.postal_code,
                        addressCountry:
                            this.business.primary_address.country_code,
                    },
                    location: {
                        "@type": "Place",
                        address: {
                            "@type": "PostalAddress",
                            streetAddress: this.business.primary_address.street,
                            addressLocality: this.business.primary_address.city,
                            addressRegion: this.business.primary_address.state,
                            postalCode:
                                this.business.primary_address.postal_code,
                            addressCountry:
                                this.business.primary_address.country_code,
                        },
                    },
                },
            ]
        },
        onSelectTimeSlot(timeSlot) {
            this.showChatModal = true
            const prefillInput = bookTimeSlot(
                this.business,
                timeSlot,
                this.jobFairName
            )
            setTimeout(() => {
                this.$root.$emit("setChatInput", prefillInput)
            }, 100)
        },
    },
    head() {
        return {
            title:
                this.business.name +
                ": Ausbildungsplätze in " +
                this.business.primary_address.city,
            link: [
                {
                    hid: "rel:canonical",
                    rel: "canonical",
                    href: this.$config.baseURL + this.$route?.path,
                },
            ],
            meta: [
                {
                    hid: "description",
                    name: "description",
                    content:
                        this.business.name +
                        " stellt sich vor. Ausbildungsplätze, Schülerpraktikum und aktuelle News vom Standort in " +
                        this.business.primary_address.city,
                },
                {
                    hid: "og:title",
                    name: "og:title",
                    content:
                        this.business.name +
                        ": Ausbildungsplätze in " +
                        this.business.primary_address.city,
                },
                {
                    hid: "og:description",
                    name: "og:description",
                    content:
                        this.business.name +
                        " stellt sich vor. Ausbildungsplätze, Schülerpraktikum und aktuelle News vom Standort in " +
                        this.business.primary_address.city,
                },
                {
                    hid: "og:type",
                    name: "og:type",
                    content: "profile",
                },
                {
                    hid: "og:image",
                    name: "og:image",
                    content: this.$getOGImage({
                        image: this.business.logo,
                    }).src,
                },
                {
                    hid: "og:image:type",
                    name: "og:image:type",
                    content: this.$getOGImage({
                        image: this.business.logo,
                    }).type,
                },
                {
                    hid: "og:image:alt",
                    name: "og:image:alt",
                    content: this.business.name + " Logo",
                },
            ],
            __dangerouslyDisableSanitizers: ["script"],
            script: [
                {
                    innerHTML: JSON.stringify(
                        this.getBreadcrumb([
                            { identifier: "business", business: this.business },
                        ])
                    ),
                    type: "application/ld+json",
                },
                {
                    innerHTML: JSON.stringify(this.getStructuredData()),
                    type: "application/ld+json",
                },
            ],
        }
    },
}
